import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { addDataPoints } from 'src/app/indexedDB/dataPointsDB/functions/addDataPoints';
import { clearDataPoints } from 'src/app/indexedDB/dataPointsDB/functions/clearDataPoints';
import { addDataPointTree } from 'src/app/indexedDB/dataPointsTreeDB/functions/addDataPointTree';
import { clearDataPointTree } from 'src/app/indexedDB/dataPointsTreeDB/functions/clearDataPointTree';
import { addDictionaries } from 'src/app/indexedDB/dictionaryDB/functions/addDictionaries';
import { clearDictionaries } from 'src/app/indexedDB/dictionaryDB/functions/clearDictionaries';
import { generateInitialSearchResultColumns } from 'src/pages/MainSearchResultsPage/api/_lib/generateInitialSearchResultColumns/generateInitialSearchResultColumns';
import { actionsCustomizeColumns } from 'src/pages/MainSearchResultsPage/api/customizeColumns/slice';
import { apiRequest } from 'src/shared/api/api';
import { RequestStatus, UrlAPI } from 'src/shared/api/types';
import { isAccessDenied } from 'src/shared/types/_guards/server_errors/isAccessDenied';
import {
	ColumnsMeta,
	ColumnsMetaRES,
	DataPoint,
	DataPointTreeBranchRES,
	DataPointTreeItem,
	DataPointTreeLeafRES,
	Dictionaries,
	ForgotPasswordREQ,
	ForgotPasswordRES,
	LoginMFAValues,
	LoginValues,
	ResetPasswordREQ,
	ResetPasswordRES,
	ThemeName,
	UserData,
} from './types';
import { Operator } from 'src/pages/MainPage/api/booleanSearch/types';
import { clearDataPointTreeBoolean } from 'src/app/indexedDB/dataPointsTreeBooleanDB/functions/clearDataPointTreeBoolean';
import { addDataPointTreeBoolean } from 'src/app/indexedDB/dataPointsTreeBooleanDB/functions/addDataPointTreeBoolean';
import { isDataPointTreeBranchRES } from 'src/shared/types/_guards/dataPointTree/isDataPointTreeBranchRES';
import { isDataPointTreeLeaf } from 'src/shared/types/_guards/dataPointTree/isDataPointTreeLeaf';
import { isDataPointTreeBranch } from 'src/shared/types/_guards/dataPointTree/isDataPointTreeBranch';

const NAME = UrlAPI.login;

// * Shared
const handleCore = createAsyncThunk(`${NAME}/login`, async (token: string, thunkAPI) => {
	const { dispatch } = thunkAPI;

	const dictionaries = await apiRequest.getRequest<Dictionaries>({
		url: UrlAPI.dictionaries,
		thunkAPI,
		token,
	});

	const columnsMetaRES = await apiRequest.getRequest<ColumnsMetaRES>({
		url: UrlAPI.columnsMetadata,
		thunkAPI,
		token,
	});

	await Promise.allSettled([dictionaries, columnsMetaRES]);

	const dataPoints: Record<string, DataPoint> = {};
	const columnsMetaEntries = Object.entries(columnsMetaRES.dataPoints);

	for (const entry of columnsMetaEntries) {
		const [key, value] = entry;

		const dataPoint: DataPoint = {
			...value,
			displayName: '', // ! Will be filled in dataPointTreeRecursiveRun
			datapointId: Number(key),
			reportingPeriodId: null,
			operators: value.operators
				.map((operatorId) => dictionaries.operators.find((dictOperator) => dictOperator.id === operatorId)?.name)
				.filter((item) => item !== undefined) as Operator[],
		};

		dataPoints[key] = dataPoint;
	}

	const dataPointTreeRecursiveRun = (item: DataPointTreeBranchRES | DataPointTreeLeafRES): DataPointTreeItem => {
		if (isDataPointTreeBranchRES(item)) {
			return {
				...item,
				children: item.children.map(dataPointTreeRecursiveRun),
			};
		} else {
			const dataPointItem = item.datapointId as number; // ! If there is no children then there is a datapointId
			dataPoints[dataPointItem] = {
				...dataPoints[dataPointItem],
				displayName: item.displayName,
			};
			return { ...(dataPoints[dataPointItem] as DataPoint), ...item } as DataPoint;
		}
	};

	// ! Same as dataPointTree but without isUnSearchable
	const buildDataPointTreeBoolean = (tree: DataPointTreeItem[]): DataPointTreeItem[] => {
		return tree.reduce((acc: DataPointTreeItem[], node): DataPointTreeItem[] => {
			if (isDataPointTreeLeaf(node) && node.isUnSearchable) {
				return acc;
			}

			const filteredChildren = isDataPointTreeBranch(node) ? buildDataPointTreeBoolean(node.children) : [];

			if (filteredChildren.length > 0) {
				acc.push({
					...node,
					children: filteredChildren,
				});
				return acc;
			}

			if (!node.children || node.children.length === 0) {
				acc.push({ ...node });
			}

			return acc;
		}, []);
	};

	const dataPointTree: DataPointTreeItem[] = columnsMetaRES.dataPointTree.map(dataPointTreeRecursiveRun);
	const dataPointTreeBoolean: DataPointTreeItem[] = buildDataPointTreeBoolean(dataPointTree);

	const columnsMeta: ColumnsMeta = {
		dataPoints,
		dataPointTree,
		dataPointTreeBoolean,
	};

	// Assign initial columns to search results page
	const initialColumns = generateInitialSearchResultColumns(columnsMeta);
	await dispatch(actionsCustomizeColumns.setInitialColumns(initialColumns));

	// Assign to local DB
	const dictionariesDB = clearDictionaries().then(() => addDictionaries(dictionaries));

	const columnsMetaDB1 = clearDataPoints().then(() => addDataPoints(dataPoints));
	const columnsMetaDB2 = clearDataPointTree().then(() => addDataPointTree(dataPointTree));
	const columnsMetaDB3 = clearDataPointTreeBoolean().then(() => addDataPointTreeBoolean(dataPointTreeBoolean));

	await Promise.allSettled([dictionariesDB, columnsMetaDB1, columnsMetaDB2, columnsMetaDB3]);
});

// * API requests
const login = createAsyncThunk(`${NAME}/login`, async (payload: LoginValues, thunkAPI) => {
	const { dispatch } = thunkAPI;

	const res = await apiRequest.postRequest<UserData>({
		url: NAME,
		payload,
		thunkAPI,
	});

	await dispatch(handleCore(res.token));

	return res;
});

const loginMFA = createAsyncThunk(`${NAME}/loginMFA`, async (payload: LoginMFAValues, thunkAPI) => {
	const { dispatch } = thunkAPI;

	const res = await apiRequest.postRequest<UserData>({
		url: `${NAME}/mfa`,
		payload,
		thunkAPI,
	});

	await dispatch(handleCore(res.token));

	return res;
});

const forgotPassword = createAsyncThunk(`${NAME}/forgotPassword`, async (arg: ForgotPasswordREQ, thunkAPI) => {
	const { params } = arg;

	return await apiRequest.postRequest<ForgotPasswordRES>({
		url: `${NAME}/password`,
		params,
		thunkAPI,
	});
});

const resetPassword = createAsyncThunk(`${NAME}/resetPassword`, async (arg: ResetPasswordREQ, thunkAPI) => {
	const { params } = arg;

	return await apiRequest.putRequest<ResetPasswordRES>({
		url: `${NAME}/password`,
		params,
		thunkAPI,
	});
});

// * Reducer
export interface State {
	activeTheme: ThemeName;
	userData: UserData | null;
	resetStatus: string | null;
	mfa: boolean;
	status: RequestStatus;
}

export const initialState: State = {
	activeTheme: 'default',
	userData: null,
	resetStatus: null,
	mfa: false,
	status: RequestStatus.still,
};

export const slice = createSlice({
	name: NAME,
	initialState,
	reducers: {
		logout: (state) => {
			state.userData = null;
		},
		storeUserData: (state, action: { payload: UserData }) => {
			state.userData = action.payload;
		},
		setTheme: (state, action: PayloadAction<'default' | 'bw'>) => {
			state.activeTheme = action.payload;
		},
		clearResetStatus: (state) => {
			state.resetStatus = null;
		},
		setMFA: (state, action: PayloadAction<boolean>) => {
			state.mfa = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(login.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(login.fulfilled, (state, action) => {
			state.userData = action.payload;
			state.status = RequestStatus.still;
		});
		builder.addCase(login.rejected, (state, action) => {
			const error = action.payload;

			if (isAccessDenied(error) && error.message.startsWith('Multi-Factor Authentication required')) {
				state.mfa = true;
			}

			state.status = RequestStatus.failed;
		});

		builder.addCase(loginMFA.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(loginMFA.fulfilled, (state, action) => {
			state.userData = action.payload;
			state.status = RequestStatus.still;
		});
		builder.addCase(loginMFA.rejected, (state) => {
			state.status = RequestStatus.failed;
		});

		builder.addCase(forgotPassword.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(forgotPassword.fulfilled, (state, action) => {
			state.status = RequestStatus.still;
			state.resetStatus = action.payload.status;
		});
		builder.addCase(forgotPassword.rejected, (state) => {
			state.status = RequestStatus.failed;
		});

		builder.addCase(resetPassword.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(resetPassword.fulfilled, (state, action) => {
			state.status = RequestStatus.still;
			state.resetStatus = action.payload.status;
		});
		builder.addCase(resetPassword.rejected, (state) => {
			state.status = RequestStatus.failed;
		});
	},
});

export const actionsAuth = {
	...slice.actions,
	login,
	loginMFA,
	forgotPassword,
	resetPassword,
};
